/**
 * Launcher V2 U1a — pure URL/copy logic for the "Where is this used"
 * sidebar. No React, no store — imported by both `UsageSidebar.tsx` (U1b)
 * and this file's own tests. See `usage-sidebar-store.ts` for the store this
 * pairs with.
 */
import { parsePlayFlowStep } from './play-flow-step';
import type { PlaybooksGenerationInputs, PlaybooksUpdatePlaysRequest, PlaybooksUsagePlace } from './schemas';
import type { PendingUsageSidebar, UsageSidebarOrigin } from './usage-sidebar-store';

/**
 * The "return to origin" link for a save/delete's own triggering surface —
 * defaults to `step=sequences` (the sidebar's origin-return historically only
 * ever pointed back at the sequences step it was opened from). `rung=` is
 * omitted for a play origin with `rungId: null` (a rung staged this session
 * with no server id yet — there is nothing to select).
 *
 * Playbooks QA round 6, U3 (item 1, H2) — `step` is now a parameter, not a
 * hardcoded literal: a SEGMENTATION gate opened from Qualify (`step=qualify`)
 * must return to Qualify, not to Sequences — the H2 bug this round fixes was
 * exactly this function always answering `step=sequences` regardless of
 * which step the save actually came from, so "Update all"/"Save a new
 * version" from a Qualify-origin gate silently landed the rep on Sequences
 * and (via `SequencesStep.tsx`'s hoisted handlers, un-guarded before this
 * round) had its decision consumed by the WRONG handler. Defaults to
 * `'sequences'` so every pre-round-6 call site's string stays byte-identical.
 */
export function originUrl(
  basePath: string,
  origin: UsageSidebarOrigin,
  step: 'sequences' | 'qualify' = 'sequences',
): string {
  if (origin.kind === 'library') {
    return `${basePath}/config?expand=${origin.targetKind}:${encodeURIComponent(origin.id)}`;
  }
  if (origin.kind === 'playbook') {
    const base = `${basePath}/playbook/${encodeURIComponent(origin.playbookId)}`;
    const parts: string[] = [];
    if (origin.seedCampaignSfdcId !== null) {
      parts.push(`campaignSfdcId=${encodeURIComponent(origin.seedCampaignSfdcId)}`);
    }
    parts.push(`step=${step}`);
    if (origin.rungId !== null) {
      parts.push(`rung=${encodeURIComponent(origin.rungId)}`);
    }
    return `${base}?${parts.join('&')}`;
  }
  const base = `${basePath}/play/${origin.playId}?campaignSfdcId=${encodeURIComponent(origin.campaignSfdcId)}&step=${step}`;
  return origin.rungId === null ? base : `${base}&rung=${encodeURIComponent(origin.rungId)}`;
}

/** Pulls the `:playId` segment out of a `/v2/play/:playId` pathname, or `null` off that route. */
function playIdFromPathname(pathname: string): string | null {
  const match = /\/play\/([^/?]+)/.exec(pathname);
  return match?.[1] ?? null;
}

/**
 * terra-qyt.13 — pulls the `:playbookId` segment out of a `/playbook/:id`
 * pathname (the playbook-only-authoring doorway), or `null` off that route.
 * A SEPARATE regex from {@link playIdFromPathname}, not a widening of it —
 * `/\/play\/([^/?]+)/` already cannot match `/playbook/` (the next char
 * after `play` is `b`, not `/`), so the two never collide. The captured
 * segment is `decodeURIComponent`-ed — callers compare it against an
 * UNENCODED `playbookId` from origin/place state.
 */
function playbookIdFromPathname(pathname: string): string | null {
  const match = /\/playbook\/([^/?]+)/.exec(pathname);
  return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
}

/**
 * `origin.rungId` tolerating a MISSING `?rung=` param — extracted so
 * {@link isOriginUnderneath}'s two identical branches (`'play'`/`'playbook'`)
 * share one rule. `null` origin rungId matches unconditionally (no
 * server-side rung to require a match against yet). Otherwise: an EXPLICIT,
 * DIFFERENT query value is a genuine mismatch (the rep navigated to another
 * rung) — `false`; a MISSING query value is treated as a match, the same
 * gap `currentUsageCardIndex`'s route-matching also tolerates: `SequencesStep.tsx`
 * doesn't always write `?rung=` for an auto-selected
 * rung, so "no rung in the URL" must not be read as "definitely not this
 * rung" when the rest of the route (play/playbook id, `?step=`) already
 * matches.
 */
function matchesOriginRung(originRungId: string | null, searchParams: URLSearchParams): boolean {
  if (originRungId === null) return true;
  const rung = searchParams.get('rung');
  return rung === null || rung === originRungId;
}

/**
 * Whether the current route is "underneath" `origin` — used to decide
 * whether the sidebar's own "return to origin" link should render (no point
 * linking back to where the rep already is). A play origin with
 * `rungId: null` matches the play route on its own, with no rung
 * requirement — there is no server-side rung to require a match against
 * yet.
 *
 * Playbooks QA round 6, U3 (item 1, H2) — `step` mirrors {@link originUrl}'s
 * new parameter, defaulting to `'sequences'` for BYTE-IDENTICAL pre-round-6
 * behavior: the default path below still does no `?step=` check at all,
 * exactly as before this round. Only the NEW `'qualify'` path adds one — a
 * SEGMENTATION gate opened from Qualify needs only a matching
 * `?step=qualify` on the SAME play/playbook, with NO `?rung=` requirement:
 * Qualify never writes `?rung=` at all (`PlayFlowShell.tsx` only reads it),
 * so requiring one made every Qualify-origin gate register as "not
 * underneath" and navigate away on every decision — the H2 bug this round
 * fixes.
 *
 * Codex fixes 2026-09-17, F3 — the `'sequences'` branch now checks the step
 * too, and both branches read it through `parsePlayFlowStep`, so a MISSING
 * `?step=` means what the shell makes it mean (`DEFAULT_PLAY_FLOW_STEP`,
 * qualify) rather than "don't care". Before this, a sequence save's sidebar
 * read "underneath" from the Qualify tab of the same play: the sidebar
 * skipped `goBackToOrigin()`, `SequencesStep` (and the drawer's host) was
 * not mounted on that tab, and the decision — a real "Update all" as much as
 * "Keep reviewing" — sat unconsumed with the footer disabled until the rep
 * happened to switch tabs. Every sequences-tab URL carries `step=sequences`
 * (`PlayFlowShell#goToStep`, `originUrl`, `usageCardNavigationUrl`), so
 * requiring it is not stricter than the routes the app writes.
 */
export function isOriginUnderneath(
  origin: UsageSidebarOrigin,
  pathname: string,
  searchParams: URLSearchParams,
  step: 'sequences' | 'qualify' = 'sequences',
): boolean {
  if (origin.kind === 'library') {
    return pathname.endsWith('/config') && searchParams.get('expand') === `${origin.targetKind}:${origin.id}`;
  }
  if (parsePlayFlowStep(searchParams.get('step')) !== step) return false;
  if (origin.kind === 'playbook') {
    if (playbookIdFromPathname(pathname) !== origin.playbookId) return false;
    return step === 'qualify' || matchesOriginRung(origin.rungId, searchParams);
  }
  if (playIdFromPathname(pathname) !== origin.playId) return false;
  return step === 'qualify' || matchesOriginRung(origin.rungId, searchParams);
}

/**
 * The composite "place" identity for an origin: `{playbookId, rungId}` for a
 * play origin, ignoring every other origin field (`playId`, `campaignSfdcId`,
 * `rungDraftKey`) — rung ids are unique only WITHIN a playbook (per
 * `PlaybookRungsSchema`'s own documented invariant), so a rung id alone is
 * ambiguous across playbooks. A library origin has no playbook/rung of its
 * own; it keys on its own target instead.
 */
export function originPlaceKey(origin: UsageSidebarOrigin): string {
  // terra-qyt.13 — a `'playbook'` origin uses the SAME `{playbookId, rungId}`
  // composite as a `'play'` origin (both have those fields, and both name
  // the identical usage place) — only `'library'` is a different surface.
  if (origin.kind === 'library') return `library:${origin.targetKind}:${origin.id}`;
  return `${origin.playbookId}:${origin.rungId ?? 'unsaved'}`;
}

/**
 * Codex fixes 2026-09-17, F2 — whether `origin` was staged in the flow
 * identified by `flow` (a play, or a playbook opened through its own
 * doorway). `originKeyOf` deliberately carries no `playId` ("one place, two
 * doorways"), so a sibling play of the SAME playbook can consume an
 * "Update all" — the write is sequence-level and lands identically. A
 * `'resume-review'` must NOT be consumed that way: the review was generated
 * for the origin play's campaign, and reopening it on a sibling play would
 * hand the rep a drawer whose Apply is refused (`CAMPAIGN_MOVED_DETAIL`).
 * `SequencesStep` passes `''` as its decision key while a resume for another
 * flow is pending, so only the origin flow's own hook can take it.
 */
export function originBelongsToFlow(
  origin: UsageSidebarOrigin,
  flow: { playId: string | undefined; playbookId: string | undefined },
): boolean {
  if (origin.kind === 'play') return origin.playId === flow.playId;
  if (origin.kind === 'playbook') return flow.playId === undefined && origin.playbookId === flow.playbookId;
  return false;
}

/**
 * Playbooks copy-fixes round — the ONE shared "N noun(s)" formatter every
 * count-driven string in this app should read off, replacing five separate
 * hardcoded `${count} places` templates in {@link sidebarCopy} (a count of 1
 * rendered "1 places" — the grammar bug a product-owner review of the
 * production sender-save sidebar caught). Grepped the repo first (team
 * convention "Reuse Before You Build") — `plays-logic.ts#playDeleteWarning`
 * deliberately avoids a pluralization branch with its own `time(s)` device
 * for a single call site's single sentence; nothing generic already existed.
 * `plural` defaults to `${singular}s` (every caller in this module is a
 * regular plural) but stays overridable for an irregular noun a future
 * caller might need.
 */
export function pluralize(count: number, singular: string, plural: string = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Plan 2026-09-29-004 (Save menu copy) — the ONE count-string builder every
 * Save-menu label reads off, beside {@link pluralize}: "Save to 1 play" /
 * "Save to all 3 plays" — never "Save to all 1 play". A fan-out or fan-in of
 * exactly one names the noun directly (singular, no "all"); anything else
 * routes through {@link pluralize} for the plural form.
 */
function saveToCount(count: number, noun: string): string {
  return count === 1 ? `Save to 1 ${noun}` : `Save to all ${pluralize(count, noun)}`;
}

/**
 * Plain-language join, "group" for the rulebook's confirm/description
 * sentences that name several playbooks: `[]` -> `''`, one name -> itself,
 * two -> "A and B", three+ -> "A, B, and C". No existing generic joiner in
 * this module (grepped first, team convention) — every prior list-of-names
 * caller had at most one name to show.
 */
function joinNames(names: readonly string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

/**
 * QA round 8, U7 (2026-09-22) — what the sidebar lists, REVERSING the
 * 2026-09-17 per-play collapse at the product owner's request: ONE card per
 * `place` x `play` (a place is one playbook RUNG, i.e. one segment's use of
 * this identity), plus one `'playbook'` card for a place whose playbook has
 * no plays yet (still a real usage — that rung maps this identity).
 *
 * Why the collapse was wrong: two rungs of ONE playbook mapping the same
 * sequence share the same plays (the read attaches a playbook's plays to
 * each of its places), so U6 folded them into one card whose `segmentNames`
 * listed both — and a rep looking at "Met with · General DQ" on a single
 * card could not tell WHICH segment "Update all" would touch, nor open just
 * one of them. Now each card names exactly one segment and links to exactly
 * one rung: two rungs sharing three plays yield SIX cards, not three.
 *
 * `placeKey` (`${playbookId}:${rungId}`) is the same composite
 * {@link originPlaceKey} builds from an origin, which is how
 * {@link currentUsageCardIndex} marks the origin card without consulting the
 * route. Order: the server's place order (playbook name, then rung
 * position), plays within a place most-recent-first as the server sorted
 * them.
 */
export type UsageCard =
  | {
      kind: 'play';
      /** `${playbookId}:${rungId}` — matches {@link originPlaceKey} for a play/playbook origin. */
      placeKey: string;
      playbookId: string;
      playbookName: string;
      rungId: string;
      /** This RUNG's segment name, or `'Segment removed'` when the segmentation is gone. */
      segmentName: string;
      playId: string;
      campaignSfdcId: string;
      campaignName: string | null;
      /** `playDisplayName`'s inputs, resolved by the caller (the sidebar has the library's playbook names). */
      playName: string | null;
      status: string;
    }
  | {
      kind: 'playbook';
      placeKey: string;
      playbookId: string;
      playbookName: string;
      rungId: string;
      segmentName: string;
    };

export function usageCards(places: readonly PlaybooksUsagePlace[]): UsageCard[] {
  const cards: UsageCard[] = [];
  for (const place of places) {
    const shared = {
      placeKey: `${place.playbookId}:${place.rungId}`,
      playbookId: place.playbookId,
      playbookName: place.playbookName,
      rungId: place.rungId,
      segmentName: place.segmentationName ?? 'Segment removed',
    };
    if (place.plays.length === 0) {
      cards.push({ kind: 'playbook', ...shared });
      continue;
    }
    for (const play of place.plays) {
      cards.push({
        kind: 'play',
        ...shared,
        playId: play.id,
        campaignSfdcId: play.campaignSfdcId,
        campaignName: play.campaignName,
        playName: play.name,
        status: play.status,
      });
    }
  }
  return cards;
}

/**
 * QA round 8, U7 — index of the card the rep is editing FROM, resolved off
 * the pending's own `origin` rather than the route. Route-matching was
 * viable while cards were per-play (one card per play id, and the route
 * carries the play id); it cannot express "which of this play's two cards"
 * now that one rung x play pair is a card and a play can appear under
 * several rungs. The origin already carries exactly the pair
 * (`{playbookId, rungId}` via {@link originPlaceKey}, plus `playId` for a
 * `'play'` origin), and it stays correct even after a card click — which,
 * since U7, opens a NEW TAB and leaves this tab's route untouched.
 *
 * `-1` (no origin card) for: a `'library'` origin (the Library row is not one
 * of the places), and any origin whose `rungId` is `null` (a rung staged this
 * session with no server id — it cannot be one of the server's places yet).
 * Both fall out of the `placeKey` comparison rather than needing their own
 * branch: `originPlaceKey` renders them `library:…` / `…:unsaved`, neither of
 * which any card can carry.
 */
export function currentUsageCardIndex(cards: readonly UsageCard[], origin: UsageSidebarOrigin): number {
  const placeKey = originPlaceKey(origin);
  if (origin.kind === 'play') {
    return cards.findIndex(
      (card) => card.placeKey === placeKey && card.kind === 'play' && card.playId === origin.playId,
    );
  }
  if (origin.kind === 'playbook') {
    const playbookCard = cards.findIndex((card) => card.placeKey === placeKey && card.kind === 'playbook');
    if (playbookCard !== -1) return playbookCard;
    return cards.findIndex((card) => card.placeKey === placeKey);
  }
  return -1;
}

export function usageCardNavigationUrl(basePath: string, card: UsageCard, linkStep: 'sequences' | 'qualify'): string {
  if (card.kind === 'playbook') {
    return `${basePath}/playbook/${encodeURIComponent(card.playbookId)}?step=${linkStep}&rung=${encodeURIComponent(card.rungId)}`;
  }
  return `${basePath}/play/${card.playId}?campaignSfdcId=${encodeURIComponent(card.campaignSfdcId)}&step=${linkStep}&rung=${encodeURIComponent(card.rungId)}`;
}

/**
 * Plan 2026-09-28-007 U9 (rulebook R4, r-7) — the DELETE sidebar's body copy,
 * replacing `sidebarCopy('delete', counts, kind)`: counts CARDS (one per
 * (group, play) pair, `usageCards`' own contract), not places/segments — a
 * single place fanned out to several plays is several groups, exactly the
 * same list-matching fix {@link usageHeadline} already applies to the save
 * sidebar's subtitle. The only branch left (plan 2026-09-28-006 U6 deleted
 * the save branch: a save's subtitle is {@link usageHeadline} and its
 * choices are {@link saveMenuOptions}).
 */
export function deleteSidebarBody(
  cards: readonly UsageCard[],
  targetKind: 'sequence' | 'segmentation' = 'sequence',
): string {
  const n = cards.length;
  const one = n === 1;
  if (n === 0) return "This can't be undone.";
  const kept = targetKind === 'segmentation' ? 'conditions' : 'steps';
  return `It's used by ${pluralize(n, 'group')}. ${one ? 'It keeps' : 'They keep'} the ${kept} it has today and ${one ? 'keeps' : 'keep'} working, but no one will be able to edit it from there anymore. This can't be undone.`;
}

/**
 * [R4] The sidebar's list-matching subtitle. ONE sentence, counting ONE
 * thing: `cards.length` — a "use", which is one (group, play) pair whose
 * ladder points at the piece, plus one "playbook" card per playbook that has
 * the piece placed but no plays yet (`usageCards`' own doc). The rep-facing
 * word for it is "place": "This sequence is used in 4 places."
 *
 * 2026-09-29 copy pass — this used to render THREE forms, each breaking the
 * total down into groups / plays / playbooks-with-no-plays-yet, while
 * {@link saveMenuOptions} counted PLAYS. That mismatch is the bug this
 * collapse fixes: a sequence used by 4 groups inside 2 plays rendered "used
 * by 4 groups in 2 plays." above a button reading "Save to all 2 plays",
 * which reads as if the button touched half of what the sentence described.
 * It never did — the 4 groups LIVE inside those 2 plays; both numbers always
 * described the identical blast radius. Now there is only one number, and
 * ②'s label is the same expression (`usageCards(places).length`), so the
 * headline, the cards and the button agree BY CONSTRUCTION rather than by
 * two counters being kept in step.
 *
 * Deleted with it: the `by N group(s) in M play(s)` shape, the `… and K
 * playbook(s) with no plays yet.` appendix and its D6 zero-play variant. A
 * zero-play "playbook" card is still a card, so it still counts toward the
 * total; it just no longer earns its own clause.
 *
 * The locked suffix (R13) is the one clause this sentence may still append. It
 * counts locked PLACES, not distinct locked plays, so it agrees with the clause
 * it follows: one sent play holding two of the places freezes two of them, and
 * counting plays there would say "1" while two places are actually frozen.
 * "them" refers back to places for the same reason — the pre-collapse wording
 * ("N of those plays") counted a different noun than the sentence before it,
 * which is exactly the mismatch this whole change removes.
 */
export function usageHeadline(cards: readonly UsageCard[], targetKind: 'sequence' | 'segmentation'): string {
  const noun = targetKind === 'segmentation' ? 'segment' : 'sequence';
  const playCards = cards.filter((card): card is Extract<UsageCard, { kind: 'play' }> => card.kind === 'play');
  const lockedPlaceCount = playCards.filter((card) => card.status === 'launched').length;

  let sentence = `This ${noun} is used in ${pluralize(cards.length, 'place')}.`;
  if (lockedPlaceCount > 0) {
    const verb = lockedPlaceCount === 1 ? 'was' : 'were';
    sentence += ` ${lockedPlaceCount} of them ${verb} already sent and won't change.`;
  }
  return sentence;
}

/**
 * [R8] The affected set option ② names on the wire
 * (`PlaybooksUpdatePlaysRequest['confirmed']`, plan 2026-09-28-007 U2): every
 * DISTINCT unsent play attached to any place, each with the groups (rung ids)
 * of it that point at the piece, plus the origin group. A launched play is
 * never included (R13 — ② skips it). Plays and rung ids are sorted so the
 * request is deterministic. The server recomputes this set under the play
 * locks and refuses any difference, so the rep's "Update N plays" acts on
 * exactly the plays and groups they were shown.
 */
/**
 * [R8][R13] Every unsent play's own groups (rungIds) that share the piece —
 * the ONE grouping both {@link confirmedFanOutSet} and {@link fanOutCounts}
 * build on, so the label (`fanOutCounts`) and the request
 * (`confirmedFanOutSet`) can never disagree about which plays or groups are
 * in the fan-out. A launched play is never counted (R13). Not itself
 * origin-scoped — `confirmedFanOutSet` is the only caller that needs an
 * `originRungId` at all (it names WHERE the confirmed set was chosen from,
 * not what's in it), so this takes no origin and invents no dummy one.
 */
function groupsByPlay(places: readonly PlaybooksUsagePlace[]): Map<string, Set<string>> {
  const rungIdsByPlayId = new Map<string, Set<string>>();
  for (const place of places) {
    for (const play of place.plays) {
      if (play.status === 'launched') continue;
      const rungIds = rungIdsByPlayId.get(play.id) ?? new Set<string>();
      rungIds.add(place.rungId);
      rungIdsByPlayId.set(play.id, rungIds);
    }
  }
  return rungIdsByPlayId;
}

export function confirmedFanOutSet(
  places: readonly PlaybooksUsagePlace[],
  originRungId: string,
): PlaybooksUpdatePlaysRequest['confirmed'] {
  const plays = [...groupsByPlay(places).entries()]
    .map(([playId, rungIds]) => ({ playId, rungIds: [...rungIds].sort() }))
    .sort((a, b) => (a.playId < b.playId ? -1 : a.playId > b.playId ? 1 : 0));
  return { originRungId, plays };
}

/**
 * [R8] The fan-out (②) counts: `plays` is the number of distinct unsent
 * plays named, `groups` the (group, play) pairs — the exact thing each
 * fanned-out copy is created for. Built from {@link groupsByPlay} directly —
 * no dummy `''` origin routed through {@link confirmedFanOutSet} just to
 * reach the same grouping.
 */
export function fanOutCounts(places: readonly PlaybooksUsagePlace[]): { plays: number; groups: number } {
  const rungIdsByPlayId = groupsByPlay(places);
  return {
    plays: rungIdsByPlayId.size,
    groups: [...rungIdsByPlayId.values()].reduce((sum, rungIds) => sum + rungIds.size, 0),
  };
}

/** {@link fallbackRetargetedPlace}'s params — everything a synthetic one-group place needs, with no field left to a hardcoded guess. */
export interface FallbackRetargetedPlaceParams {
  playbookId: string;
  playbookName: string;
  rungId: string;
  segmentationId: string;
  segmentationName: string;
  sequenceId: string;
  sequenceName: string;
  playId: string;
  campaignSfdcId: string;
}

/**
 * Plan 2026-09-28-007 (REVIEW-THESE follow-up) — the one-group place
 * `SequencesStep.tsx#handlePlaySettingsSaveNew` retargets onto when the
 * origin rung's own place is missing from the pending's usage snapshot
 * (`pending.places`, frozen at the moment the Save menu opened).
 * `inPlayUsageGate` guarantees that place is present whenever the menu is
 * reached through the normal gate — a `'stale-origin'` result would have
 * refused the write before any menu opened — so this is a defensive
 * fallback for a caller or race that doesn't hold that invariant, not a
 * reachable UI path today.
 *
 * `places: []` used to be that fallback. {@link saveMenuOptions}'s
 * owned-and-one-group short-circuit (`target.owningPlayId !== null &&
 * places.length === 1`) reads `places.length`, so an EMPTY array skipped it
 * and fell through to the ordinary menu — which still offers "Save as new —
 * this group only" a SECOND time, forking a second copy on the very next
 * click. Building a one-group place from the rung itself instead keeps the
 * count at exactly 1, so the retargeted menu collapses to the single owned
 * "Save".
 */
export function fallbackRetargetedPlace(params: FallbackRetargetedPlaceParams): PlaybooksUsagePlace {
  return {
    playbookId: params.playbookId,
    playbookName: params.playbookName,
    rungId: params.rungId,
    segmentationId: params.segmentationId,
    segmentationName: params.segmentationName,
    sequenceId: params.sequenceId,
    sequenceName: params.sequenceName,
    flowId: null,
    flowName: null,
    plays: [{ id: params.playId, campaignSfdcId: params.campaignSfdcId, campaignName: null, status: 'draft', name: null }],
    playCount: 1,
    unsentPlayCount: 1,
  };
}

/** [R6] One option in the in-play Save menu, or the two-option doorway menu. */
export interface SaveMenuOption {
  decision: 'save-new' | 'update-plays' | 'update-all';
  label: string;
  description: string;
  confirm?: { title: string; body: string; confirmLabel: string };
}

/**
 * [R6] Builds every option of the ONE Save menu, for every kind of in-play
 * edit (steps, sender, CC/reply-to, flow id, rebind, rename, AI Apply,
 * segment filters) and for the two master doorways (Library page, playbook
 * page) — no other button, dialog or path inside a play writes a Library
 * piece. Arity ONE: no role/context argument (Spencer, 2026-09-28 — there is
 * no admin gate on ③; the confirm dialog naming the playbook(s) is the only
 * safeguard).
 *
 * Doorway origins (`'playbook'`, `'library'`) keep today's two-option,
 * no-confirm menu — R12: on a master doorway the rep is editing the shared
 * thing on purpose.
 *
 * [R4][R6] 2026-09-29 copy pass, round 2 — THE IN-PLAY MENU COUNTS EXACTLY
 * ONE THING: `uses`, i.e. `usageCards(pending.places).length`, the same
 * expression {@link usageHeadline} renders. Before this pass the labels
 * counted PLAYS while the headline counted GROUPS, so "This sequence is used
 * by 4 groups in 2 plays." sat above "Save to all 2 plays" — implying the
 * button reached half of what the sentence described. It never did: the 4
 * groups live INSIDE those 2 plays, one blast radius described twice. Now
 * the headline, the cards and ②'s label are the same number by construction,
 * so they cannot drift.
 *
 * In-play origins (`'play'`) get up to three:
 *  ① always — "Save and create new {noun}" / "Only this group uses it."
 *  ② one label shape, NO branching (this is the whole point) —
 *    "{@link saveToCount}(uses, `${noun} use`)", i.e. "Save to all 4 sequence
 *    uses" and "Save to 1 sequence use" at one (never "all 1", edge (k)) —
 *    with the one flat subline "Update everywhere except the playbook." at
 *    every count, for both target kinds and for owned and Library targets
 *    alike. Only the VISIBILITY predicate still differs by ownership ([R6]
 *    "hidden when it would change only the origin group"): an OWNED target
 *    needs more than one place, a LIBRARY target more than one
 *    {@link fanOutCounts} `groups` axis.
 *  ③ a LIBRARY target only, hidden for an AI pending ([R17]
 *    `generationInputs`) and for a piece archived in the Library ([R18]
 *    `target.archivedInLibrary`) — never gated on role. "Save everywhere and
 *    update the playbook", carrying NO count and identical however many uses
 *    or playbooks there are; subline "Every net new and existing play gets
 *    these updates." Edge (g): when no playbook's CURRENT template
 *    references the piece today (`pending.templatePlaybooks.length === 0`),
 *    the label and confirm name the Library instead — "Save everywhere and
 *    update the Library", subline "Every play still sharing the Library
 *    version gets this." (D3). `confirm.title`/`body` are unchanged by this
 *    pass and still name exactly `pending.templatePlaybooks` (Codex b1-6) —
 *    never a place's `playbookName`, which can be a play-only place whose
 *    template has since moved the rung elsewhere (R9's own invariant).
 *    `confirm.confirmLabel` always equals the label.
 *
 * KNOWN AND ACCEPTED (Spencer, 2026-09-29), do not "fix" or caveat: ③'s
 * "everywhere" is not literal. It writes the Library version, which reaches
 * every group still following it plus every future play, but NOT a group
 * that already froze its own copy (R10, copy-on-write). Reviewed and shipped
 * as written — no asterisk, no extra confirm.
 *
 * Deleted by this pass, all under "count exactly one thing": the owned
 * target's "{@link saveToCount} groups in this play" label and its D5
 * subline; the Library target's same-play variant of that label (edge (i) —
 * there is no longer ANY branch on whether every use sits in one play); the
 * "…across M playbooks" variant and the segment-target "This changes who
 * each of those plays sends to." appendix (edge (d)); and ③'s
 * "{@link saveToCount} plays and …" count prefix.
 */
export function saveMenuOptions(pending: PendingUsageSidebar): SaveMenuOption[] {
  const noun = pending.target.kind === 'segmentation' ? 'segment' : 'sequence';

  if (pending.origin.kind === 'playbook' || pending.origin.kind === 'library') {
    // Codex fixes 2026-09-28-007 (b1-4) — counts CARDS (one per (group,
    // play) pair), not `places.length`: one place fanned out to several
    // plays is several groups, the exact list `usageCards` renders below.
    const cards = usageCards(pending.places);
    const playCards = cards.filter((card) => card.kind === 'play');
    const playbookOnlyCards = cards.filter((card) => card.kind === 'playbook');
    const playbookIds = new Set(playbookOnlyCards.map((card) => card.playbookId));

    let updateAllOption: SaveMenuOption;
    if (playCards.length === 0 && playbookIds.size === 1) {
      // D4 (2026-09-29) — nothing has been made from this playbook yet: name
      // the playbook directly rather than a card count.
      updateAllOption = {
        decision: 'update-all',
        label: 'Save to the playbook',
        description: 'Every future play made from it gets this.',
      };
    } else if (playCards.length === 0 && playbookIds.size > 1) {
      updateAllOption = {
        decision: 'update-all',
        label: `Save to all ${playbookIds.size} playbooks`,
        description: 'Every future play made from them gets this.',
      };
    } else {
      updateAllOption = {
        decision: 'update-all',
        label: saveToCount(cards.length, 'group'),
        description: `Every group still using this ${noun} changes together.`,
      };
    }

    return [
      updateAllOption,
      {
        decision: 'save-new',
        label: `Save as new ${noun}`,
        description:
          pending.origin.kind === 'playbook'
            ? 'Plays already made from this playbook keep the original.'
            : `Creates a new ${noun} in the Library; nothing already using this one changes.`,
      },
    ];
  }

  // Plan 2026-09-28-007 U6 (rulebook R7, Codex c1-3) — an owned target used
  // by this one group alone: the settings "this group only" fork already made
  // this copy and the write after it failed (`retargetUsageSidebarPending`).
  // The only sensible retry writes onto the copy in place (②'s owned write);
  // a "Save as new" here would fork a second copy for the same edit.
  if (pending.target.owningPlayId !== null && pending.places.length === 1) {
    return [{ decision: 'update-plays', label: 'Save', description: "Saves onto this group's own copy." }];
  }

  const options: SaveMenuOption[] = [
    { decision: 'save-new', label: `Save and create new ${noun}`, description: 'Only this group uses it.' },
  ];

  const isOwned = pending.target.owningPlayId !== null;

  // [R4][R6] U — the ONE number this menu counts, and the same expression
  // `usageHeadline` renders, so the button can never disagree with the
  // sentence above the cards. NOT `fanOutCounts().plays` (the old ② count)
  // and NOT `places.length` (a place with two plays is two uses, two cards).
  const uses = usageCards(pending.places).length;

  // [R6] Ownership still decides WHETHER ② appears ("hidden when it would
  // change only the origin group") — it no longer decides what ② SAYS.
  const showFanOut = isOwned ? pending.places.length > 1 : fanOutCounts(pending.places).groups > 1;

  if (showFanOut) {
    // Edge (d)/(i) — one label, one subline, no conditionals: not on the
    // target kind, not on `ownVersionPlays`, not on how many playbooks the
    // uses span, and not on whether they all sit in the current play.
    options.push({
      decision: 'update-plays',
      label: saveToCount(uses, `${noun} use`),
      description: 'Update everywhere except the playbook.',
    });
  }

  if (!isOwned && !pending.generationInputs && !pending.target.archivedInLibrary) {
    // Plan 2026-09-28-007 U9 (rulebook R9, Codex b1-6) — ③ names EXACTLY
    // `pending.templatePlaybooks` (the usage response's own field, sourced
    // from each playbook's CURRENT template ladder) — never a name harvested
    // from `pending.places`, which can include a play-only place whose
    // template has since moved the rung elsewhere.
    if (pending.templatePlaybooks.length === 0) {
      // Edge (g) — the Library named in place of the playbook. Like every ③
      // label it carries no count.
      const label = 'Save everywhere and update the Library';
      options.push({
        decision: 'update-all',
        label,
        // D3 (2026-09-29) — ③'s ordinary subline isn't true when no playbook
        // uses the piece today; name what's actually true.
        description: 'Every play still sharing the Library version gets this.',
        confirm: {
          // Rulebook edge (g), r-11 — "Change the Library version?", not
          // "Change the playbook too?": there is no playbook to name here.
          title: 'Change the Library version?',
          body: 'No playbook uses this piece today; this changes the Library version every play still sharing it will send.',
          confirmLabel: label,
        },
      });
    } else {
      const names = pending.templatePlaybooks.map((playbook) => playbook.name);
      // No count, and the SAME string whether one playbook or five are
      // named — the confirm body below still names each of them.
      const label = 'Save everywhere and update the playbook';
      const body =
        names.length > 1
          ? `This changes ${names.length} playbooks: ${joinNames(names)}. Every new play made from them will use this version, and so will every play that hasn't made its own copy.`
          : `This changes the "${names[0]}" playbook. Every new play made from it will use this version, and so will every play that hasn't made its own copy.`;
      options.push({
        decision: 'update-all',
        label,
        description: 'Every net new and existing play gets these updates.',
        confirm: { title: 'Change the playbook too?', body, confirmLabel: label },
      });
    }
  }

  return options;
}

/**
 * 2026-09-17 QA follow-ups, U3 — the Generate session the usage sidebar
 * should hand back to when a rep dismisses an "Apply to sequence" save
 * without deciding. `openUsageSidebar` closes the Generate DRAWER (OQ4) but
 * the staged review survives, so after the X the rep was left on a page of
 * Accept/Reject columns with no Apply, no Discard, and the hand-edit Save
 * hidden — the only way back was a button labelled "Generate with AI". This
 * maps the pending request's origin back onto the drawer's (scope, sequence,
 * surface) triple: a `'library'` origin is the Library pane's fixed
 * `'library'` scope; a `'play'`/`'playbook'` origin's scope is the rung's
 * `draftKey`, exactly what `SequencesStep` mounts the drawer under.
 * `restoreFrom` is the pending's own `generationInputs` (D6), so the drawer
 * restores the form that produced the staged copy. `null` for anything that
 * is not an AI-originated SEQUENCE save (hand-edit saves, sender saves,
 * segmentation saves, deletes) — those keep the plain close.
 */
export function generateSessionForPending(
  pending: Pick<PendingUsageSidebar, 'mode' | 'target' | 'origin' | 'generationInputs' | 'draftSettings'>,
): { scope: string; sequenceId: string; surface: 'embedded' | 'library'; restoreFrom: PlaybooksGenerationInputs } | null {
  if (pending.mode !== 'save' || pending.target.kind !== 'sequence') return null;
  if (pending.draftSettings !== undefined || !pending.generationInputs) return null;
  if (pending.origin.kind === 'library') {
    return { scope: 'library', sequenceId: pending.target.id, surface: 'library', restoreFrom: pending.generationInputs };
  }
  return { scope: pending.origin.rungDraftKey, sequenceId: pending.target.id, surface: 'embedded', restoreFrom: pending.generationInputs };
}
